import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, Share, X } from 'lucide-react';
import { useCopy } from '../../i18n';
import { usePwaLifecycle } from '../../pwa/usePwaLifecycle';
import { useIsMobile } from '../../hooks/useIsMobile';
import { Button } from '../ui/button';
import analytics from '../../services/analyticsService';

const CHAVE_CONVITE_VISTO = 'meugantt_pwa_convite_visto';

/**
 * Os dois avisos do app instalável: "tem versão nova" e "dá para instalar".
 *
 * Nenhum dos dois aparece na chegada. Convite de instalação na primeira tela é
 * o padrão que treina o usuário a fechar banner sem ler: ele ainda não sabe se
 * quer o produto, então instalar não faz sentido para ele. Aqui o convite só
 * nasce depois que a pessoa navegou de verdade dentro do app.
 *
 * A atualização usa toast e não modal de propósito: ela não é urgente e não
 * pode interromper. Quem estiver escrevendo continua escrevendo.
 */
const PwaPrompts = () => {
  const t = useCopy().common.pwa;
  const isMobile = useIsMobile();
  const { updateReady, updateApp, canInstall, install, isStandalone, isIOS } = usePwaLifecycle();
  const [convitePronto, setConvitePronto] = useState(false);

  // Versão nova: toast que não some sozinho. Some sozinho seria pior do que não
  // avisar, porque o usuário veria um lampejo e não saberia o que perdeu.
  useEffect(() => {
    if (!updateReady) return;
    toast(t.updateTitle, {
      description: t.updateDescription,
      duration: Infinity,
      action: { label: t.updateAction, onClick: () => updateApp() },
    });
  }, [updateReady, updateApp, t]);

  // O convite espera a segunda navegação: até lá a pessoa ainda está decidindo
  // se o produto serve, e instalar é pedir compromisso antes da entrega.
  useEffect(() => {
    if (isStandalone) return;
    try {
      if (localStorage.getItem(CHAVE_CONVITE_VISTO)) return;
    } catch { /* storage indisponível */ }

    let navegacoes = 0;
    const contar = () => {
      navegacoes += 1;
      if (navegacoes >= 2) {
        setConvitePronto(true);
        window.removeEventListener('popstate', contar);
      }
    };
    window.addEventListener('popstate', contar);
    // `pushState` não emite evento: o app navega por ele (ver useAppRouteSync),
    // então o contador precisa de um empurrão por temporizador também.
    const t1 = window.setTimeout(() => setConvitePronto(true), 45000);
    return () => {
      window.removeEventListener('popstate', contar);
      window.clearTimeout(t1);
    };
  }, [isStandalone]);

  const dispensar = () => {
    try { localStorage.setItem(CHAVE_CONVITE_VISTO, '1'); } catch { /* ignore */ }
    setConvitePronto(false);
  };

  const instalar = async () => {
    analytics.trackPwaInstallPrompt?.('accepted');
    await install();
    dispensar();
  };

  // Só no celular: instalar no desktop existe, mas o ganho real (ícone na tela
  // de início, push no iOS, abrir sem barra de navegador) é de telefone.
  if (isStandalone || !isMobile || !convitePronto) return null;
  if (!canInstall && !isIOS) return null;

  return (
    <div
      data-testid="pwa-install-invite"
      className="hidden max-md:flex fixed bottom-0 left-0 right-0 z-30 items-start gap-3 p-4 pb-[calc(1rem+4.5rem+env(safe-area-inset-bottom))] bg-[var(--color-bg-secondary)] border-t border-[var(--color-border)] shadow-lg"
    >
      <Download size={20} className="shrink-0 mt-0.5 text-[var(--color-primary)]" />
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-[var(--color-text)] mb-0.5">{t.installTitle}</p>
        {isIOS ? (
          // No iPhone não há diálogo nativo para chamar: o único caminho é
          // ensinar o gesto, então a frase cita o menu compartilhar por nome.
          <p className="text-[11px] text-[var(--color-text-secondary)] flex items-center gap-1 flex-wrap">
            <Share size={12} className="shrink-0" />
            {t.installIOS}
          </p>
        ) : (
          <p className="text-[11px] text-[var(--color-text-secondary)]">{t.installDescription}</p>
        )}
      </div>
      {!isIOS && (
        <Button variant="primary" size="sm" onClick={instalar}>
          {t.installAction}
        </Button>
      )}
      <button
        onClick={dispensar}
        aria-label={t.dismiss}
        className="shrink-0 w-7 h-7 flex items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)]"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default PwaPrompts;
